import React, { useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import Button from "@/shared/components/Button/Button";
import Title from "@/shared/components/Title/Title";
import { inputStyles } from "@/utils/constants";
import formattedDate from "@/utils/formattedDate";
import priceFormatter from "@/utils/priceFormatter";
import { totalPriceCounter } from "@/services/API/totalPriceCounter";
import { dateFormatter } from "@/utils/dateFormatter";
import { updateExistAppointment } from "@/store/appointments/appointmentsSlice";
import { updatePaymentHistory } from "@/store/patients/patients";
import { toast } from "react-toastify";
import useUpdateEntity from "@/hooks/useUpdateEntity";
import { setLoading } from "@/store/ui/ui";

import { IPatientAppointmentItemProps } from "@/shared/interfaces/patientPage";

import "../PatientDetailsPageAppsList/PatientDetailsPageAppsList.scss";

export default function PatientAppointmentItem({
    appointment,
    index,
    handleOpen,
}: IPatientAppointmentItemProps) {
    const dispatch = useDispatch();
    const { handleUpdate } = useUpdateEntity();
    const [inputValue, setInputValue] = useState<string>("");
    const [expanded, setExpanded] = useState<string | false>(false);

    const appointmentId = appointment._id;
    const paymentHistory = appointment.paymentHistory;
    const services = appointment.services;
    const totalServiceSum = totalPriceCounter(services);
    const totalPayment = paymentHistory.reduce(
        (accum, item) => accum + Number(item.amount),
        0,
    );
    const debt = totalServiceSum - totalPayment;

    const handleChange =
        (panel: string) =>
        (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^[0-9]*$/.test(value) && value.length < 7) setInputValue(value);
    };

    function updatelocalData() {
        const date = dateFormatter(new Date());
        const newPayment = {
            date: date,
            amount: String(inputValue),
        };
        const newPaymentHistory = [newPayment, ...paymentHistory];

        dispatch(
            updateExistAppointment({
                paymentHistory: [...newPaymentHistory],
            }),
        );
        dispatch(
            updatePaymentHistory({
                id: appointmentId,
                paymentHistory: newPaymentHistory,
            }),
        );
        setInputValue("");
    }
    async function savePaidAmount() {
        if (inputValue.length > 0) {
            const newPayment = {
                date: dateFormatter(new Date()),
                amount: String(inputValue),
            };
            if (totalPayment + +inputValue <= totalServiceSum) {
                const newPaymentHistory = [
                    newPayment,
                    ...(paymentHistory || []),
                ];
                dispatch(setLoading(true));
                await handleUpdate({
                    entity: "appointments",
                    id: appointmentId ?? "",
                    data: { paymentHistory: [...newPaymentHistory] },
                    onSuccess: updatelocalData,
                });
                dispatch(setLoading(false));
            } else {
                toast.error("Введена сума більша за вартість наданих послуг");
            }
        }
    }

    return (
        <Accordion
            key={appointment._id}
            expanded={expanded === `panel${index + 1}`}
            onChange={handleChange(`panel${index + 1}`)}
            sx={{
                backgroundColor: debt > 0 ? "#fdf2f2" : "#F6F9F9",
                border: debt > 0 ? "1px solid #f5c6c6" : "1px solid #DCE0E5",
                boxShadow: "none",
                mb: 2,
                borderRadius: "4px",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel-content'
                id='panel-header'
            >
                <div className='summary_accordeon_container'>
                    <p className='summary_accordeon_time'>
                        {formattedDate(appointment.date)}{" "}
                        <span>{`${appointment?.startTime} - ${appointment?.endTime}`}</span>
                    </p>
                    <p className='summary_accordeon_doctor'>
                        {`Лікар:   `}
                        {appointment?.doctorId?.name
                            ? appointment?.doctorId?.name
                            : "Дані відсутні"}
                    </p>
                    <p className='summary_accordeon_doctor'>
                        {appointment?.hasVisited
                            ? "Візит відбувся"
                            : "Візит не відбувся"}
                    </p>
                    <div className='stat_listing_item_payment_container'>
                        <p className='stat_listing_item_total'>
                            <span>Всього:</span>
                            <span>{priceFormatter(totalServiceSum)}</span>
                        </p>
                        <p className='stat_listing_item_payment'>
                            <span>Сплачено:</span>
                            <span>{priceFormatter(totalPayment)}</span>
                        </p>
                        <p className='stat_listing_item_debt'>
                            <span>Борг:</span>
                            <span>{priceFormatter(debt)}</span>
                        </p>
                    </div>
                </div>
            </AccordionSummary>

            <AccordionDetails>
                <Title tag='h4' text='Послуги' style={{ color: "black" }} />
                <ul className='stat_listing_item_service_listing'>
                    {appointment.services.length > 0 ? (
                        appointment.services.map(service => (
                            <li
                                className='stat_listing_item_service_item'
                                key={service._id}
                            >
                                <p>{service.title}</p>
                                <dl className='stat_listing_item_service_dlist'>
                                    <div className='stat_quantity'>
                                        <dt>Кл-ть</dt>
                                        <dd>{service.quantity}</dd>
                                    </div>
                                    <div className='stat_discount'>
                                        <dt>%</dt>
                                        <dd>{service.discount}</dd>
                                    </div>
                                    <div className='stat_price'>
                                        <dd>
                                            {priceFormatter(service.price)} грн
                                        </dd>
                                    </div>
                                </dl>
                            </li>
                        ))
                    ) : (
                        <p className='stat_listing_paragraf'>
                            Послуги відсутні
                        </p>
                    )}
                </ul>
                <Title
                    tag='h4'
                    text='Платежі'
                    style={{ color: "black", marginTop: 20 }}
                />
                <ul className='stat_listing_item_service_listing listing_payments'>
                    {appointment.paymentHistory.length > 0 ? (
                        appointment.paymentHistory.map(payment => (
                            <li
                                className='stat_listing_item_service_item'
                                key={payment.amount + payment.date}
                            >
                                <div className='stat_listing_item_payment_container'>
                                    <p className='stat_listing_item_payment'>
                                        Дата:{" "}
                                        <span>
                                            {formattedDate(payment.date)}
                                        </span>
                                    </p>
                                </div>
                                <div className='stat_listing_item_payment_container'>
                                    <p className='stat_listing_item_payment'>
                                        Сума:{" "}
                                        <span>
                                            {priceFormatter(payment.amount)}
                                        </span>
                                    </p>
                                </div>
                            </li>
                        ))
                    ) : (
                        <p className='stat_listing_paragraf'>
                            Платежі відсутні
                        </p>
                    )}
                </ul>
                <Button
                    variant='delete-small'
                    text='Видалити візит'
                    onClick={() => handleOpen(appointmentId)}
                />
                <div style={{ marginTop: "20px", display: "flex" }}>
                    <Button
                        variant='edit-small'
                        text='Внести оплату'
                        onClick={savePaidAmount}
                        style={{ width: "50%", marginRight: "12px" }}
                    />
                    <TextField
                        type='text'
                        label='Введіть суму оплати'
                        sx={{
                            ...inputStyles.root,
                            width: "50%",
                            height: "44px",
                            outline: "none",
                            "& .MuiInputBase-root": { height: "44px" },
                            "& .MuiInputBase-input": { padding: "10px" },
                            "& .MuiFormLabel-root": { top: "-6px" },
                        }}
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                </div>
            </AccordionDetails>
        </Accordion>
    );
}
